import { FunctionComponent, useState } from 'react'
import {
    OccasionalSlot,
    OccasionalSlotPriorityChild,
    OccasionalSlotPriorityGroup,
    OccasionalSlotsPlaces
} from '@/types/Slots'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import {
    CalendarMonth,
    ChildCareOutlined,
    ContentCopy,
    Delete,
    EventBusy,
    Forum,
    Grade,
    Group,
    LockClock,
    LockOpen,
    Message,
    Person,
    Place,
    StickyNote2
} from '@mui/icons-material'
import moment from 'moment/moment'
import styles from './showOccasionalSlotDialog.module.scss'
import { DaycareType } from '@/types/Daycare'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import DeleteOccasionalSlotDialogComponent from '../../occasionalSlot/deleteOccasionalSlot/deleteOccasionalSlotDialog'
import UnbookOccasionalSlot from '../../occasionalSlot/unbookOccasionalSlot/unbookOccasionalSlot'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'
import { ChildWithParents } from '@/types/Child'
import { Parent } from '@/types/Parent'
import DuplicateOccasionalSlotDialog from '../../occasionalSlot/duplicateOccasionalSlot/duplicateOccasionalSlot'
import ReleaseOccasionalSlotDialog from '../../occasionalSlot/releaseOccasionalSlot/releaseOccasionalSlot'
import { getOccasionalSlotsIconTextToJSX } from '@/modules/helper/iconsHelper'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { format, formatDistanceToNow, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { Role } from '@/types/Role.ts'
import EditCalendar from '@mui/icons-material/EditCalendar'
import Info from '@mui/icons-material/Info'
import MessageWithURL from '@/components/_custom/MessageWithUrl'
import { isPastDate, isWithinEditionLimit, titleWithPastLabel } from '@/modules/helper/editionLimitHelper'
import { useDialog } from '@/hooks/useDialog/useDialog'
import OccasionalSlotForm, {
    EditBaseProps,
    OccasionalSlotFormProps
} from '../../occasionalSlot/occasionalSlotForm/occasionalSlotForm'
import DialogActions from '@/components/_custom/DialogActions/DialogActions'
import BookOccasionalSlot from '../../occasionalSlot/bookOccasionalSlot/bookOccasionalSlot'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { useDaycareRight } from '@/hooks/useDaycareRight'

interface ShowOccasionalSlotDialogProps {
    handleClose: () => void
    occasionalSlotType: CalendarEventType
    occasionalSlots: OccasionalSlot[]
    refetchData: () => void
    childs: ChildWithParents[]
    calendarEvents: EventCalendar[]
    places: OccasionalSlotsPlaces[]
    isParent: boolean
    parent?: Parent
}

export type OccasionalSlotReducer = {
    id: number
    childName: string
    childComplement: string
}

type ChildIdAndChildName = {
    childId: number
    childName: string
}

const ShowOccasionalSlotDialog: FunctionComponent<ShowOccasionalSlotDialogProps> = ({
    handleClose,
    occasionalSlotType,
    occasionalSlots,
    refetchData,
    childs,
    calendarEvents,
    places,
    isParent,
    parent
}) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const canEditOccasional: boolean = useDaycareRight('canWriteOccasionalSlot')

    const navigate: NavigateFunction = useNavigate()

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    const occasionalSlot: OccasionalSlot = occasionalSlots[0]

    const { dialog: occasionalSlotFormDialog, openDialog: openOccasionalSlotFormDialog } = useDialog({
        Component: OccasionalSlotForm,
        props: (onClose: () => void): OccasionalSlotFormProps => {
            const commonProps: EditBaseProps = {
                handleClose: onClose,
                closeParentDialog: handleClose,
                refetchData,
                childs,
                calendarEvents,
                places
            }
            return occasionalSlots.length === 1
                ? { ...commonProps, type: 'edit', occasionalSlot }
                : { ...commonProps, type: 'multiEdit', occasionalSlots }
        },
        dialogTitle: t('editOccasionalSlot'),
        hideDialogClose: true
    })

    const { dialog: BookOccasionalSlotDialog, openDialog: openBookOccasionalSlotDialog } = useDialog({
        Component: BookOccasionalSlot,
        props: (handleClose) => ({
            occasionalSlots,
            refetchData,
            closeParentDialog: handleClose,
            handleClose,
            parent
        }),
        dialogTitle: t('book'),
        hideDialogClose: true
    })

    const childNameList: ChildIdAndChildName[] = occasionalSlots
        ? occasionalSlots
              .filter((slot: OccasionalSlot): boolean => slot.childName !== null && slot.childId !== null)
              .map((slot: OccasionalSlot): ChildIdAndChildName => {
                  return {
                      childName: slot.childName + ' ' + (slot.childComplement ? slot.childComplement : ''),
                      childId: slot.childId!
                  }
              })
        : []

    const priorityGroupList: string[] = occasionalSlot.priorityGroup
        ? (occasionalSlot.priorityGroup
              .map((group: OccasionalSlotPriorityGroup): string | null => (group.name ? group.name : null))
              .filter((groupName: string | null): boolean => groupName !== null) as string[])
        : []

    const priorityChildList: string[] = occasionalSlot.priorityChild
        ? (occasionalSlot.priorityChild
              .map((child: OccasionalSlotPriorityChild): string | null => (child.firstname ? child.firstname : null))
              .filter((childName: string | null): boolean => childName !== null) as string[])
        : []

    const [isDuplicateOccasionalSlotDialogOpen, setIsDuplicateOccasionalSlotDialogOpen] = useState<boolean>(false)
    const [isReleaseOccasionalSlotDialogOpen, setIsReleaseOccasionalSlotDialogOpen] = useState<boolean>(false)

    const [isCancelOccasionalSlotDialogOpen, setIsCancelOccasionalSlotDialogOpen] = useState<boolean>(false)

    function title(): string {
        switch (occasionalSlotType) {
            case CalendarEventType.AVAILABLE:
                return t('availableSlot')
            case CalendarEventType.SCHEDULED:
                return t('scheduledSlot')
            case CalendarEventType.VALIDATED:
                return t('validatedSlot')
            case CalendarEventType.REMAINDER_AVAILABLE:
                return t('remainderAvailableSlot')
            case CalendarEventType.EXPIRED:
                return t('expiredSlot')
            default:
                return t('availableSlot')
        }
    }

    const isPast: boolean = isPastDate(occasionalSlot.date)

    const { dialog: DeleteOccasionalSlotDialog, openDialog: openDeleteOccasionalSlotDialog } = useDialog({
        dialogTitle: true,
        Component: DeleteOccasionalSlotDialogComponent,
        props: (onClose) => ({
            handleClose: onClose,
            occasionalSlots,
            occasionalSlotType,
            refetchData,
            closeParentDialog: handleClose
        }),
        hideDialogClose: true
    })

    return (
        <>
            <DialogHeader title={titleWithPastLabel(title(), isPast, t)} parent={isParent} />
            <div className={styles.Container}>
                <CalendarMonth className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                <div className={styles.TextContainer}>
                    <p className={styles.FirstText}>
                        {format(parse(occasionalSlot.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                            locale: getLocalFromLanguage()
                        })}
                    </p>
                    <p className={styles.SecondText}>
                        {t('from') +
                            ' ' +
                            moment(occasionalSlot.start, 'HH:mm:ss').format('HH:mm') +
                            ' ' +
                            t('to').toLowerCase() +
                            ' ' +
                            moment(occasionalSlot.end, 'HH:mm:ss').format('HH:mm')}
                    </p>
                </div>
            </div>
            {occasionalSlot.section && (
                <div className={styles.Container}>
                    <Group className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <div className={styles.TextContainer}>
                        <p className={styles.FirstText}>{occasionalSlot.section}</p>
                    </div>
                </div>
            )}
            <div className={styles.BreakLine}></div>
            {isRpeOrLaep && occasionalSlot.parentName && (
                <div className={styles.SecondContainer}>
                    <Person
                        className={`${styles.Icon} ${isParent ? styles.IconParent : styles.ClickableIconDaycare}`}
                        onClick={() => !isParent && navigate(`/adults?parent=${occasionalSlot.parentId}`)}
                    />
                    <p className={styles.Text}>{occasionalSlot.parentName}</p>
                </div>
            )}
            {childNameList.length > 0 &&
                childNameList.map((childName: ChildIdAndChildName, index: number) => (
                    <div className={styles.SecondContainer} key={index}>
                        <ChildCareOutlined
                            className={`${styles.Icon} ${isParent ? styles.IconParent : styles.ClickableIconDaycare}`}
                            onClick={() => !isParent && navigate(`/childs?child=${childName.childId}`)}
                        />
                        <p className={styles.Text}>{childName.childName}</p>
                    </div>
                ))}
            {occasionalSlot.parentComment && (
                <div className={styles.SecondContainer}>
                    <Message className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <MessageWithURL message={occasionalSlot.parentComment} />
                </div>
            )}
            {(priorityGroupList.length > 0 || priorityChildList.length > 0) && (
                <div className={styles.SecondContainer}>
                    <Grade className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>
                        {priorityGroupList.length > 0 &&
                            priorityGroupList.map((groupName, index) => (
                                <p className={styles.Text} key={index}>
                                    {groupName}
                                </p>
                            ))}
                        {priorityChildList.length > 0 &&
                            priorityChildList.map((childName, index) => (
                                <p className={styles.Text} key={index}>
                                    {childName}
                                </p>
                            ))}
                    </p>
                </div>
            )}
            {occasionalSlot.scheduled && moment(occasionalSlot.scheduled, 'DD-MM-YYYY HH:mm').isAfter(moment()) && (
                <div className={styles.SecondContainer}>
                    <LockClock className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>
                        {format(
                            parse(occasionalSlot.scheduled, 'dd-MM-yyyy HH:mm:ss', new Date()),
                            'EEEE dd MMMM yyyy HH:mm',
                            { locale: getLocalFromLanguage() }
                        ) +
                            ' - ' +
                            formatDistanceToNow(parse(occasionalSlot.scheduled, 'dd-MM-yyyy HH:mm:ss', new Date()), {
                                locale: getLocalFromLanguage(),
                                addSuffix: true
                            })}
                    </p>
                </div>
            )}
            {occasionalSlotType !== CalendarEventType.VALIDATED && occasionalSlot.expiry && (
                <div className={styles.SecondContainer}>
                    <EventBusy className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>
                        {format(
                            parse(occasionalSlot.expiry, 'dd-MM-yyyy HH:mm:ss', new Date()),
                            'EEEE dd MMMM yyyy HH:mm',
                            {
                                locale: getLocalFromLanguage()
                            }
                        ) +
                            ' - ' +
                            formatDistanceToNow(parse(occasionalSlot.expiry, 'dd-MM-yyyy HH:mm:ss', new Date()), {
                                locale: getLocalFromLanguage(),
                                addSuffix: true
                            })}
                    </p>
                </div>
            )}
            {occasionalSlot.placeId && (
                <div className={styles.SecondContainer}>
                    <Place className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>{occasionalSlot.placeName + ' ' + occasionalSlot.placeAddress}</p>
                </div>
            )}
            {occasionalSlot.internalNote && (
                <div className={styles.SecondContainer}>
                    <StickyNote2 className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <MessageWithURL message={occasionalSlot.internalNote} />
                </div>
            )}
            {occasionalSlot.externalNote && (
                <div className={styles.SecondContainer}>
                    {occasionalSlot.externalIcon ? (
                        getOccasionalSlotsIconTextToJSX(
                            occasionalSlot.externalIcon,
                            `${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`
                        )
                    ) : (
                        <Forum className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    )}
                    <MessageWithURL message={occasionalSlot.externalNote} />
                </div>
            )}
            {showThirdBreakLine(
                occasionalSlot,
                occasionalSlotType,
                priorityGroupList,
                priorityChildList,
                childNameList,
                isRpeOrLaep
            ) &&
                ((user?.role === Role.cald && occasionalSlot.updated) ||
                    occasionalSlotType === CalendarEventType.REMAINDER_AVAILABLE) && (
                    <div className={styles.BreakLine}></div>
                )}
            {user?.role === Role.cald && occasionalSlot.updated && (
                <div className={styles.SecondContainer}>
                    <EditCalendar className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>
                        {format(
                            parse(occasionalSlot.updated, 'dd-MM-yyyy HH:mm:ss', new Date()),
                            'EEEE dd MMMM yyyy HH:mm',
                            {
                                locale: getLocalFromLanguage()
                            }
                        )}
                    </p>
                </div>
            )}
            {occasionalSlotType === CalendarEventType.REMAINDER_AVAILABLE && (
                <div className={styles.SecondContainer}>
                    <Info className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <div
                        className={styles.TextContainer}
                        onClick={() => navigate(`/daycare?daycare=${daycare.id}#merger`)}
                    >
                        <p className={styles.Text}>{t('remainderAvailableInfo')}</p>
                        <p className={styles.Text}>
                            {t('uCanModifyParameters')}
                            <span className={styles.Link}>{t('clickHere')}</span>
                        </p>
                    </div>
                </div>
            )}
            <DialogActions block>
                <div className={styles.ButtonLine}>
                    {(isParent ||
                        !(
                            occasionalSlotType === CalendarEventType.AVAILABLE ||
                            occasionalSlotType === CalendarEventType.REMAINDER_AVAILABLE ||
                            occasionalSlotType === CalendarEventType.SCHEDULED ||
                            occasionalSlotType === CalendarEventType.VALIDATED
                        )) && (
                        <ButtonTextIcon
                            type={'close'}
                            onClick={handleClose}
                            className={`${isParent ? styles.ButtonParent : styles.Button}
                                ${moment(occasionalSlot.date, 'DD-MM-YYYY').isSameOrBefore(moment(), 'day') ? styles.ButtonFullWidth : ''}`}
                            parent={isParent}
                            fullWidth
                            nowrap
                        />
                    )}
                    {(moment(occasionalSlot.date, 'DD-MM-YYYY').format('DD-MM-YYYY') ===
                        moment().format('DD-MM-YYYY') ||
                        isWithinEditionLimit(occasionalSlot.date)) &&
                        (isParent
                            ? (occasionalSlotType === CalendarEventType.VALIDATED &&
                                  moment(
                                      moment(occasionalSlot.date, 'DD-MM-YYYY').format('DD-MM-YYYY') +
                                          ' ' +
                                          moment(occasionalSlot.start, 'HH:mm:ss').format('HH:mm:ss'),
                                      'DD-MM-YYYY HH:mm:ss'
                                  ).isAfter(moment()) && (
                                      <ButtonTextIcon
                                          type={'custom'}
                                          customText={t('cancel')}
                                          customIcon={<Delete />}
                                          onClick={() => setIsCancelOccasionalSlotDialogOpen(true)}
                                          className={styles.ButtonParent}
                                          parent={isParent}
                                          nowrap
                                      />
                                  )) ||
                              occasionalSlotType === CalendarEventType.REMAINDER_AVAILABLE ||
                              (occasionalSlotType === CalendarEventType.AVAILABLE && (
                                  <ButtonTextIcon
                                      type={'custom'}
                                      customText={t('book')}
                                      customIcon={<LockClock />}
                                      onClick={openBookOccasionalSlotDialog}
                                      className={styles.ButtonParent}
                                      parent={isParent}
                                      nowrap
                                  />
                              ))
                            : canEditOccasional && (
                                  <>
                                      {(occasionalSlotType === CalendarEventType.AVAILABLE ||
                                          occasionalSlotType === CalendarEventType.REMAINDER_AVAILABLE ||
                                          occasionalSlotType === CalendarEventType.SCHEDULED ||
                                          occasionalSlotType === CalendarEventType.VALIDATED) && (
                                          <ButtonTextIcon
                                              nowrap
                                              type={'custom'}
                                              customText={t('duplicate')}
                                              customIcon={<ContentCopy />}
                                              onClick={() => setIsDuplicateOccasionalSlotDialogOpen(true)}
                                          />
                                      )}
                                      {occasionalSlotType === CalendarEventType.SCHEDULED && (
                                          <ButtonTextIcon
                                              nowrap
                                              type={'custom'}
                                              customText={t('release')}
                                              customIcon={<LockOpen />}
                                              onClick={() => setIsReleaseOccasionalSlotDialogOpen(true)}
                                          />
                                      )}
                                      <ButtonTextIcon nowrap type={'delete'} onClick={openDeleteOccasionalSlotDialog} />
                                      <ButtonTextIcon nowrap type={'edit'} onClick={openOccasionalSlotFormDialog} />
                                  </>
                              ))}
                </div>
                {!isParent &&
                    (occasionalSlotType === CalendarEventType.AVAILABLE ||
                        occasionalSlotType === CalendarEventType.REMAINDER_AVAILABLE ||
                        occasionalSlotType === CalendarEventType.SCHEDULED ||
                        occasionalSlotType === CalendarEventType.VALIDATED) && (
                        <ButtonTextIcon nowrap type={'close'} onClick={handleClose} parent={isParent} fullWidth />
                    )}
            </DialogActions>
            {DeleteOccasionalSlotDialog}
            {isDuplicateOccasionalSlotDialogOpen && (
                <DuplicateOccasionalSlotDialog
                    open={isDuplicateOccasionalSlotDialogOpen}
                    daycareId={daycare.id}
                    handleClose={() => setIsDuplicateOccasionalSlotDialogOpen(false)}
                    closeParentDialog={handleClose}
                    occasionalSlotId={occasionalSlot.id}
                    refetchData={refetchData}
                />
            )}
            {isReleaseOccasionalSlotDialogOpen && (
                <ReleaseOccasionalSlotDialog
                    open={isReleaseOccasionalSlotDialogOpen}
                    handleClose={() => setIsReleaseOccasionalSlotDialogOpen(false)}
                    occasionalSlots={occasionalSlots.map((slot: OccasionalSlot): OccasionalSlotReducer => {
                        return {
                            id: slot.id,
                            childName: '',
                            childComplement: ''
                        }
                    })}
                    daycareId={daycare.id}
                    refetchData={refetchData}
                    closeParentDialog={handleClose}
                />
            )}
            {isCancelOccasionalSlotDialogOpen && (
                <UnbookOccasionalSlot
                    open={isCancelOccasionalSlotDialogOpen}
                    handleClose={() => setIsCancelOccasionalSlotDialogOpen(false)}
                    occasionalSlots={occasionalSlots}
                    daycare={daycare}
                    refetchData={refetchData}
                    closeParentDialog={handleClose}
                />
            )}
            {BookOccasionalSlotDialog}
            {occasionalSlotFormDialog}
        </>
    )
}

export default ShowOccasionalSlotDialog

function showThirdBreakLine(
    occasionalSlot: OccasionalSlot,
    occasionalSlotType: CalendarEventType,
    priorityGroupList: string[],
    priorityChildList: string[],
    childNameList: ChildIdAndChildName[],
    isRpeOrLaep: boolean
): boolean {
    return (
        !!occasionalSlot.externalNote ||
        !!occasionalSlot.internalNote ||
        !!occasionalSlot.placeId ||
        (occasionalSlotType !== CalendarEventType.VALIDATED && !!occasionalSlot.expiry) ||
        (occasionalSlot.scheduled && moment(occasionalSlot.scheduled, 'DD-MM-YYYY HH:mm').isAfter(moment())) ||
        priorityGroupList.length > 0 ||
        priorityChildList.length > 0 ||
        !!occasionalSlot.parentComment ||
        childNameList.length > 0 ||
        (isRpeOrLaep && !!occasionalSlot.parentName)
    )
}
